import { ComponentType, lazy } from "react";

/**
 * MODULE_REGISTRY — Single source of truth for all frontend module metadata.
 *
 * Keyed by the stable `key` identifier that the backend sends in the menu API.
 * The backend only provides: key, path, children, tabs (structural/access data).
 * The frontend enriches with: label (i18n key), icon, component, tabComponent.
 *
 * When adding a new page:
 *   1. Add the entry here (label + component)
 *   2. Create the module folder under src/modules/
 *   3. That's it — no other file to update.
 *
 * When adding a new tab:
 *   1. Add the entry here (label + tabComponent)
 *   2. Create the tab component file
 *   3. That's it.
 */

export interface IModuleRegistryEntry {
  /** i18n translation key for display name */
  label: string;
  /** Ant Design icon component name (only for nav categories) */
  icon?: string;
  /** Lazy-loaded page component (only for leaf pages that render content) */
  component?: ComponentType<any>;
  /** Props to pass to the page component (e.g. view="realDSN") */
  props?: Record<string, any>;
  /** Lazy-loaded tab component (only for tab entries) */
  tabComponent?: ComponentType<any>;
}

// ══════════════════════════════════════════════════════════════════════════════
// MODULE REGISTRY — All pages, tabs & navigation in one overview
// ══════════════════════════════════════════════════════════════════════════════

const MODULE_REGISTRY: Record<string, IModuleRegistryEntry> = {
  // ── Navigation Categories ──────────────────────────────────────────────────
  ROOT: { label: "navigation.home" },
  DEFINE: { label: "navigation.define", icon: "SettingOutlined" },
  PLAN: { label: "navigation.plan", icon: "FileOutlined" },
  EXECUTE: { label: "navigation.execute", icon: "EditOutlined" },
  ANALYZE: { label: "navigation.analyze", icon: "BarChartOutlined" },
  MONITOR: { label: "navigation.monitor", icon: "DashboardOutlined" },
  ADMIN: { label: "navigation.admin", icon: "TeamOutlined" },

  // ── Pages ──────────────────────────────────────────────────────────────────
  HOME: { label: "navigation.home", component: lazy(() => import("@modules/Home")) },
  HOMEPAGE: { label: "navigation.homePage", icon: "HomeOutlined", component: lazy(() => import("@modules/Home")) },
  TEST: { label: "navigation.test", component: lazy(() => import("@modules/Test")) },
  TEST_SPEC: { label: "navigation.testSpec", component: lazy(() => import("@modules/TestSpec")) },
  TEST_SPEC_CM: { label: "navigation.testSpecCM", component: lazy(() => import("@modules/TestSpec")), props: { view: "noSpecProgress" } },
  FAMILY_SPEC: { label: "navigation.familySpec", component: lazy(() => import("@modules/FamilySpec")) },
  TASK_CENTER: { label: "navigation.taskCenter", component: lazy(() => import("@modules/DataInput")) },
  REAL_DSN: { label: "navigation.realDSN", component: lazy(() => import("@modules/DataInput")), props: { view: "realDSN" } },
  DATA_OVERVIEW: { label: "navigation.dataOverview", component: lazy(() => import("@modules/DataOverview")) },
  READOUT_INPUT: { label: "navigation.readoutInput", component: lazy(() => import("@modules/ReadoutInput")) },
  TEST_WATERFALL: { label: "navigation.testWaterfall", component: lazy(() => import("@modules/TestWaterfall")) },
  FDDD: { label: "navigation.fddd", component: lazy(() => import("@modules/FDDD")) },
  TEST_STATUS: { label: "navigation.testStatus", component: lazy(() => import("@modules/TestStatus")) },
  TEST_SUMMARY: { label: "navigation.testSummary", component: lazy(() => import("@modules/Reports/TestSummary")) },
  OPERATOR_MANAGEMENT: { label: "navigation.operatorManagement", component: lazy(() => import("@modules/OperatorManagement")) },
  TEST_SETTINGS: { label: "navigation.testSettings", component: lazy(() => import("@modules/TestSettings")) },
  TEST_ASSIGNMENT: { label: "navigation.testAssignment", component: lazy(() => import("@modules/DataInput")), props: { view: "testAssignment" } },
  ISSUE_LIST: { label: "navigation.issueList", component: lazy(() => import("@modules/DataInput/IssueList")) },
  EQUIPMENT_BOOKING: { label: "navigation.equipmentBooking", component: lazy(() => import("@modules/EquipmentBooking")) },

  // ── TestWaterfall Tabs ─────────────────────────────────────────────────────
  TEST_WATERFALL_DETAILS: { label: "tabs.testWaterfallDetails", tabComponent: lazy(() => import("@modules/TestWaterfall/WaterfallDetails")) },
  TIMELINE_DETAILS: { label: "tabs.timelineDetails", tabComponent: lazy(() => import("@modules/TestWaterfall/TimelineDetails")) },
  TIMELINE_ATTACHMENTS: { label: "tabs.attachments", tabComponent: lazy(() => import("@modules/TestWaterfall/Attachments")) },

  // ── TestSummary Tabs ───────────────────────────────────────────────────────
  ISSUE_SUMMARY: { label: "tabs.issueSummary", tabComponent: lazy(() => import("@modules/Reports/TestSummary/IssueTab")) },
  TEST_RESULT: { label: "tabs.testResult", tabComponent: lazy(() => import("@modules/Reports/TestSummary/TestResultTab")) },
  TEST_SUMMARY_ATTACHMENTS: { label: "tabs.attachments", tabComponent: lazy(() => import("@modules/Reports/TestSummary/AttachmentTab")) },

  // ── FA Tracker ─────────────────────────────────────────────────────────────
  FA_TRACKER: { label: "navigation.faTracker", component: lazy(() => import("@modules/FATracker")) },
  FATP_VIEWER: { label: "navigation.fatpViewer", component: lazy(() => import("@modules/FATPViewer")) },
  UV_VISUALIZER: { label: "navigation.uvVisualizer", component: lazy(() => import("@modules/UVVisualizer")) },

  // ── FA Tracker Tabs ────────────────────────────────────────────────────────
  FA_LOG: { label: "tabs.faLog", tabComponent: lazy(() => import("@modules/FATracker/FALogTab")) },
  FA_NEW_FINDINGS: { label: "tabs.newFindings", tabComponent: lazy(() => import("@modules/FATracker/NewFindingsTab")) },

  // ── ESD Calculator ─────────────────────────────────────────────────────────
  ESD_CALCULATOR: { label: "navigation.esdCalculator", component: lazy(() => import("@modules/ESDCalculator")) },

  // ── ESD Calculator Tabs ────────────────────────────────────────────────────
  ESD_MANUAL_INPUT: { label: "tabs.manualInput", tabComponent: lazy(() => import("@modules/ESDCalculator/ManualInputTab")) },
  ESD_PROJECT_DATA: { label: "tabs.projectData", tabComponent: lazy(() => import("@modules/ESDCalculator/ProjectDataTab")) },
  ESD_TEST_PLAN: { label: "tabs.testPlan", tabComponent: lazy(() => import("@modules/ESDCalculator/TestPlanTab")) },

  // ── Ball Impact Calculator ──────────────────────────────────────────────────
  BALL_IMPACT_CALCULATOR: { label: "navigation.ballImpactCalculator", component: lazy(() => import("@modules/BallImpactCalculator")) },

  // ── Ball Impact Calculator Tabs ─────────────────────────────────────────────
  BALL_IMPACT_MANUAL_INPUT: { label: "tabs.manualInput", tabComponent: lazy(() => import("@modules/BallImpactCalculator/ManualInputTab")) },
  BALL_IMPACT_PROJECT_DATA: { label: "tabs.projectData", tabComponent: lazy(() => import("@modules/BallImpactCalculator/ProjectDataTab")) },

  // ── Stress-Strength Calculator ──────────────────────────────────────────────
  ARR_CALCULATOR: { label: "navigation.stressStrengthCalculator", component: lazy(() => import("@modules/StressStrengthCalculator")) },

  // ── Approval Center ─────────────────────────────────────────────────────────
  APPROVAL: { label: "navigation.approval", component: lazy(() => import("@modules/ApprovalCenter")) },
};

export default MODULE_REGISTRY;
